import type { MouseEvent } from 'react';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, MenuItem } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { IosShare } from '@mui/icons-material';
import { Button } from 'components/mui/common/Button';

export type ExportType = 'Csv' | 'Pdf' | 'Zip' | 'ZipWithItems';

interface Props {
  onClick: (type: ExportType) => void;
  disabled?: boolean;
  variant?: 'outlined' | 'contained' | 'text';
  sx?: SxProps<Theme>;
}

export const ExportButton = memo(({ onClick, disabled = false, variant = 'outlined', sx }: Props) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback(
    (type: ExportType) => () => {
      onClick(type);
      handleClose();
    },
    [handleClose, onClick]
  );

  return (
    <>
      <Button
        disabled={disabled}
        type="button"
        variant={variant}
        size="small"
        startIcon={<IosShare sx={{ transform: 'rotate(90deg)' }} />}
        onClick={handleOpen}
        sx={sx}>
        {t('flow.export.title')}
      </Button>
      <Menu disableScrollLock anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleClick('Csv')}>{t('flow.export.toCsv')}</MenuItem>
        <MenuItem onClick={handleClick('Pdf')}>{t('flow.export.toPdf')}</MenuItem>
        <MenuItem onClick={handleClick('Zip')}>{t('flow.export.toZipWithAttachments')}</MenuItem>
        <MenuItem onClick={handleClick('ZipWithItems')}>{t('flow.export.toZipWithAttachmentsAndItems')}</MenuItem>
      </Menu>
    </>
  );
});
